import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, setToken } from "@/lib/api";
import { Loader } from "@/components/Loader";

import NurseAuth from "@/pages/NurseAuth";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/admin/AdminLogin";
import NurseShell from "@/components/nurse/NurseShell";
import NurseHome from "@/pages/nurse/NurseHome";
import Library from "@/pages/nurse/Library";
import ModuleView from "@/pages/nurse/ModuleView";
import Assessment from "@/pages/nurse/Assessment";
import Results from "@/pages/nurse/Results";
import ProgressPage from "@/pages/nurse/ProgressPage";
import NotificationsPage from "@/pages/nurse/NotificationsPage";
import ProfilePage from "@/pages/nurse/ProfilePage";
import AdminShell from "@/components/admin/AdminShell";
import Dashboard from "@/pages/admin/Dashboard";
import NursesPage from "@/pages/admin/NursesPage";
import NurseDetail from "@/pages/admin/NurseDetail";
import AccessRequests from "@/pages/admin/AccessRequests";
import ModulesPage from "@/pages/admin/ModulesPage";
import ModuleEditor from "@/pages/admin/ModuleEditor";
import ReattemptsPage from "@/pages/admin/ReattemptsPage";
import GradingPage from "@/pages/admin/GradingPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import AdminNotifications from "@/pages/admin/AdminNotifications";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(location.hash.replace("#", ""));
    const sessionId = params.get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setToken(data.token);
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/app", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line
  return <Loader label="Signing you in..." />;
}

function Protected({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : "/login"} replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Landing />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<NurseAuth />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      <Route path="/app" element={<Protected role="nurse"><NurseShell /></Protected>}>
        <Route index element={<NurseHome />} />
        <Route path="library" element={<Library />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      <Route path="/app/modules/:id" element={<Protected role="nurse"><ModuleView /></Protected>} />
      <Route path="/app/modules/:id/assessment" element={<Protected role="nurse"><Assessment /></Protected>} />
      <Route path="/app/results/:id" element={<Protected role="nurse"><Results /></Protected>} />

      <Route path="/admin" element={<Protected role="admin"><AdminShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="nurses" element={<NursesPage />} />
        <Route path="nurses/:id" element={<NurseDetail />} />
        <Route path="access-requests" element={<AccessRequests />} />
        <Route path="modules" element={<ModulesPage />} />
        <Route path="modules/:id" element={<ModuleEditor />} />
        <Route path="reattempts" element={<ReattemptsPage />} />
        <Route path="grading" element={<GradingPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="notifications" element={<AdminNotifications />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
